import { Component, inject, input, output, ViewChild } from '@angular/core';
import { CancellationFlowStatus } from 'src/app/_enums/cancellation/cancellation-flow-status.enum';
import { CancellationFlowDetailsResponse } from 'src/app/_types/cancellation.types';
import { CancellationLetterCardComponent } from 'src/app/shared/components/cancellation-cards/cancellation-letter-card.component';
import { CancellationLinkCardComponent } from 'src/app/shared/components/cancellation-cards/cancellation-link-card.component';
import { CancellationFinalizeModalComponent } from 'src/app/shared/components/cancellation-finalize-modal/cancellation-finalize-modal.component';
import { CancellationLetterModalComponent } from 'src/app/shared/components/cancellation-letter-modal/cancellation-letter-modal.component';
import { CancellationSendModalComponent } from 'src/app/shared/components/cancellation-send-modal/cancellation-send-modal.component';

import { TaskCancellationsService } from './task-cancellations.service';

/**
 * Above the stepper: the opened flow's running cancellation, if any, as the
 * two cards it is worked from — its replacement and its letter. Nothing
 * otherwise.
 */
@Component({
  selector: 'app-task-running-cancellation',
  standalone: true,
  imports: [
    CancellationLinkCardComponent,
    CancellationLetterCardComponent,
    CancellationLetterModalComponent,
    CancellationSendModalComponent,
    CancellationFinalizeModalComponent,
  ],
  template: `
    @if (store.running; as cancellation) {
      <div class="task-running-cancellation">
        <app-cancellation-link-card
          target="replacement"
          [cancellation]="cancellation"
          (changed)="store.changed($event)"
          (viewFlow)="viewFlow.emit($event)"
        />
        <app-cancellation-letter-card
          [cancellation]="cancellation"
          (action)="openLetterForm(cancellation)"
        />
      </div>
    }

    <!-- Outside the cards: finalising ends the cancellation, and its form outlives them. The slide-in sits above the affaire's own sheet, which is the host's layer. -->
    <app-cancellation-letter-modal
      #letterModal
      [layer]="layer() + 1"
      (changed)="store.changed($event)"
    />
    <app-cancellation-send-modal #sendModal (changed)="store.changed($event)" />
    <app-cancellation-finalize-modal
      #finalizeModal
      (changed)="store.settled($event)"
    />
  `,
  styles: `
    :host {
      display: block;
    }

    /*
     * The sheet's row logic: what lands on a line shares it. The stepper below
     * has no padding of its own, so the band's 16px and the design's 24px
     * above the stepper all sit here.
     */
    .task-running-cancellation {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      margin-bottom: 40px;
    }

    .task-running-cancellation > * {
      display: flex;
      flex: 1 1 min(560px, 100%);
      flex-direction: column;
    }
  `,
})
export class TaskRunningCancellationComponent {
  protected readonly store = inject(TaskCancellationsService);

  /** The host sheet's layer; the letter form opens one above it. */
  readonly layer = input(0);

  /** « Voir l'affaire » on the replacement: the flow the host should open. */
  readonly viewFlow = output<string>();

  @ViewChild('letterModal')
  private readonly letterModal?: CancellationLetterModalComponent;

  @ViewChild('sendModal')
  private readonly sendModal?: CancellationSendModalComponent;

  @ViewChild('finalizeModal')
  private readonly finalizeModal?: CancellationFinalizeModalComponent;

  /** The letter card's button opens the form its status calls for, as on the sheet. */
  protected openLetterForm(
    cancellation: CancellationFlowDetailsResponse
  ): void {
    if (cancellation.status === CancellationFlowStatus.SIGNED)
      this.sendModal?.openModal(cancellation);
    else if (cancellation.status === CancellationFlowStatus.SENT)
      this.finalizeModal?.openModal(cancellation);
    else this.letterModal?.openModal(cancellation);
  }
}
